import { useMemo, useState } from 'react';
import { Avatar, Box, Button, Grid, IconButton, Stack, Typography } from '@mui/material';
import { ChevronRight } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import classnames from 'classnames';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import NetworkCheckIcon from '@mui/icons-material/NetworkCheck';
import { CrossIcon, CrownIcon, HelpIcon, HomeIcon, LogOut2Icon, PencilIcon, SettingsIcon } from 'assets';
import { ConnectionTestDialog } from 'components/ConnectionTest/ConnectionTestDialog';
import { IBurgerMenuContext, IUserContext } from 'context';
import { IApiRequests, useApiRequests, useAzureAsset, useBurgerMenu, useThemePreference, useUser } from 'hooks';
import { useAppSelector } from 'store';
import { selectUser } from 'store/user';
import { ApiUser, SubscriptionStatusEnum } from 'types';
import { getFullName } from 'utils';
import usFlag from 'assets/images/us-flag.svg';
import mxFlag from 'assets/images/mx-flag.svg';
import brFlag from 'assets/images/br-flag.svg';
import frFlag from 'assets/images/fr-flag.svg';
import zhFlag from 'assets/images/zh-flag.svg';
import ruFlag from 'assets/images/ru-flag.svg';
import hiFlag from 'assets/images/hi-flag.svg';
import arFlag from 'assets/images/ar-flag.svg';
import { createThemeOptions, getVisibleThemePreference, renderThemeIcon, visibleThemePreferences } from 'theme';

import styles from './BurgerMenu.module.scss';

export function BurgerMenu() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const { t, i18n } = useTranslation(['providerRoom', 'auth']);
  const [isThemeOpen, setIsThemeOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [connectionTestOpen, setConnectionTestOpen] = useState(false);
  const { themePreference, setThemePreference } = useThemePreference();
  const themePreferenceForUi = getVisibleThemePreference(themePreference);

  const selectedLanguage =
    i18n.resolvedLanguage === 'es' ||
    i18n.resolvedLanguage === 'br' ||
    i18n.resolvedLanguage === 'fr' ||
    i18n.resolvedLanguage === 'zh' ||
    i18n.resolvedLanguage === 'ru' ||
    i18n.resolvedLanguage === 'hi' ||
    i18n.resolvedLanguage === 'ar'
      ? i18n.resolvedLanguage
      : 'en';

  const activeLanguage = (i18n.resolvedLanguage || i18n.language || selectedLanguage || 'en').toLowerCase();
  const isRtl = activeLanguage === 'ar' || activeLanguage.startsWith('ar-') || i18n.dir() === 'rtl';

  const languageOptions: Array<{
    code: 'en' | 'es' | 'br' | 'fr' | 'zh' | 'ru' | 'hi' | 'ar';
    label: string;
    flag: string;
  }> = [
    { code: 'en', label: 'English', flag: usFlag },
    { code: 'es', label: 'Español', flag: mxFlag },
    { code: 'br', label: 'Português', flag: brFlag },
    { code: 'fr', label: 'Français', flag: frFlag },
    { code: 'zh', label: '中文', flag: zhFlag },
    { code: 'ru', label: 'Русский', flag: ruFlag },
    { code: 'hi', label: 'हिन्दी', flag: hiFlag },
    { code: 'ar', label: 'العربية', flag: arFlag },
  ];

  const selectedLanguageOption =
    languageOptions.find((option) => option.code === selectedLanguage) || languageOptions[0];

  const themeOptions = useMemo(
    () =>
      createThemeOptions({
        light: t('providerRoom:light', { defaultValue: 'Light' }),
        dark: t('providerRoom:dark', { defaultValue: 'Dark' }),
        system: t('providerRoom:system', { defaultValue: 'System' }),
        calm: t('providerRoom:calm', { defaultValue: 'Calm' }),
        nature: t('providerRoom:nature', { defaultValue: 'Nature' }),
      }).filter((option) => visibleThemePreferences.includes(option.mode)),
    [t],
  );

  const { isOpen, hideMenu }: IBurgerMenuContext = useBurgerMenu();
  const user: ApiUser | null = useAppSelector(selectUser);
  const { logout }: IUserContext = useUser();
  const { updateClinicUser }: IApiRequests = useApiRequests();
  const avatarUrl = useAzureAsset(user?.linkToProfilePicture);
  const subscriptionInfo = user?.clinicUser.subscriptionInfo;
  const subscriptionStatus = subscriptionInfo?.status;
  const dayMs = 1000 * 60 * 60 * 24;

  const getRemainingDays = (targetDate?: string | null): number => {
    if (!targetDate) {
      return 0;
    }

    const referenceNow = subscriptionInfo?.serverTime ? new Date(subscriptionInfo.serverTime) : new Date();
    const remaining = Math.ceil((new Date(targetDate).getTime() - referenceNow.getTime()) / dayMs);
    return remaining > 0 ? remaining : 0;
  };

  const isTrial = subscriptionStatus === SubscriptionStatusEnum.Trial;
  const isNonRenewing = subscriptionStatus === SubscriptionStatusEnum.NonRenewing;
  const isExpiredOrCancelled =
    subscriptionStatus === SubscriptionStatusEnum.Expired || subscriptionStatus === SubscriptionStatusEnum.Cancelled;
  const isPaymentFailed =
    subscriptionStatus === SubscriptionStatusEnum.Dunning || subscriptionStatus === SubscriptionStatusEnum.Unpaid;

  const handleHelpClick = (): void => {
    window.location.replace('https://support.hipaalink.net/');
  };

  const handleHomeClick = (): void => {
    navigate('/');
    hideMenu();
  };

  const handleWaitingRoomClick = (): void => {
    navigate('/edit');
    hideMenu();
  };

  const handleSettingsClick = (): void => {
    navigate('/settings', { state: { fromBurgerMenu: true } });
    hideMenu();
  };

  const handleLogoutClick = (): void => {
    navigate('/');
    hideMenu();
    logout();
  };

  const handleSubscriptionBannerClick = (): void => {
    hideMenu();

    if (isNonRenewing) {
      navigate('/settings#subscriptions');
      return;
    }

    navigate('/subscriptionDetails');
  };

  const subscriptionBanner = useMemo(() => {
    if (!subscriptionInfo || !(isTrial || isNonRenewing || isExpiredOrCancelled || isPaymentFailed)) {
      return null;
    }

    const remainingDays = isTrial
      ? getRemainingDays(subscriptionInfo.trialEndsAt)
      : isNonRenewing
      ? getRemainingDays(subscriptionInfo.currentTermEndsAt)
      : 0;

    return {
      actionText: isNonRenewing ? t('keepYourPlan') : t('upgradeNow'),
      bannerText: isTrial
        ? `${t('youHave')} ${t('daysRemainingPeriod', { days: remainingDays })}`
        : isNonRenewing
        ? `${t('youHave')} ${t('daysRemainingPeriod', { days: remainingDays })}`
        : isPaymentFailed
        ? t('paymentFailed')
        : t('subscriptionNoLongerActive'),
      isAlert: isExpiredOrCancelled || isPaymentFailed,
      isTrial,
      isNonRenewing,
      severityClass:
        isTrial || isNonRenewing
          ? remainingDays <= 2
            ? styles.subscriptionBannerCritical
            : remainingDays <= 5
            ? styles.subscriptionBannerWarning
            : styles.subscriptionBannerHealthy
          : styles.subscriptionBannerAlert,
    };
  }, [getRemainingDays, isExpiredOrCancelled, isNonRenewing, isPaymentFailed, isTrial, subscriptionInfo, t]);

  const handleThemeClick = (): void => {
    setIsThemeOpen((value) => !value);
    setIsLanguageOpen(false);
  };

  const handleLanguageClick = (): void => {
    setIsLanguageOpen((value) => !value);
    setIsThemeOpen(false);
  };

  const handleThemeSelect = (mode: typeof themePreference): void => {
    setThemePreference(mode);
    setIsThemeOpen(false);
  };

  const handleLanguageSelect = (language: 'en' | 'es' | 'br' | 'fr' | 'zh' | 'ru' | 'hi' | 'ar'): void => {
    i18n.changeLanguage(language);
    setIsLanguageOpen(false);
    if (user) {
      updateClinicUser({ languageCode: language }).catch(() => undefined);
    }
  };
  const isMyRoomSelected = location.pathname === '/';
  const isEditWaitingRoomSelected = location.pathname.startsWith('/edit');
  const isSettingsSelected = location.pathname.startsWith('/settings');

  const currentThemeIcon = useMemo(
    () => renderThemeIcon(themePreferenceForUi, styles.trailingIcon),
    [themePreferenceForUi],
  );

  return (
    <>
      {isOpen && (
        <Box className={styles.background}>
          <Box className={styles.burgerMenu}>
            <Stack className={styles.header}>
              <IconButton onClick={hideMenu}>
                <CrossIcon color='primary' />
              </IconButton>
            </Stack>

            {subscriptionBanner && (
              <Box className={styles.subscriptionStatus}>
                <Box className={classnames(styles.subscriptionBanner, subscriptionBanner.severityClass)}>
                  <Box className={styles.subscriptionBannerText}>{subscriptionBanner.bannerText}</Box>
                  <Button className={styles.subscriptionBannerButton} onClick={handleSubscriptionBannerClick}>
                    {subscriptionBanner.actionText}
                  </Button>
                </Box>
              </Box>
            )}

            <Stack
              className={styles.profile}
              sx={{
                borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'var(--app-field-divider)',
              }}
            >
              <Avatar className={styles.profilePicture} src={avatarUrl} />

              <Grid className={styles.texts}>
                <Typography component='h4' variant='h4' className={styles.name}>
                  {getFullName(user)}
                </Typography>

                <Typography component='p' variant='body2' color='var(--app-text-muted)'>
                  {user?.clinicUser.organization}
                </Typography>

                <CrownIcon className={styles.crownIcon} />
              </Grid>
            </Stack>

            <Stack
              className={classnames(styles.items, {
                [styles.itemsRtl]: isRtl,
              })}
              sx={{
                '& .MuiTypography-root': {
                  color: isDarkMode ? '#FFFFFF' : 'var(--app-text-default)',
                },
                '& .MuiSvgIcon-root': {
                  color: isDarkMode ? '#FFFFFF' : 'var(--app-text-default)',
                },
                '& svg': {
                  color: isDarkMode ? '#FFFFFF' : 'var(--app-text-default)',
                },
              }}
            >
              <div
                className={classnames(styles.item, { [styles.selected]: isMyRoomSelected })}
                onClick={handleHomeClick}
              >
                <HomeIcon />
                <Typography component='p' variant='body2'>
                  {t('providerRoom:myRoomTab')}
                </Typography>
                <ChevronRight className={styles.right} />
              </div>

              <div
                className={classnames(styles.item, { [styles.selected]: isEditWaitingRoomSelected })}
                onClick={handleWaitingRoomClick}
              >
                <PencilIcon />
                <Typography component='p' variant='body2'>
                  {t('providerRoom:editWaitingRoomTab')}
                </Typography>
                <ChevronRight className={styles.right} />
              </div>

              <div className={styles.devider}></div>

              <div
                className={classnames(styles.item, { [styles.selected]: isSettingsSelected })}
                onClick={handleSettingsClick}
              >
                <SettingsIcon />
                <Typography component='p' variant='body2'>
                  {t('providerRoom:menus.settings')}
                </Typography>
                <ChevronRight className={styles.right} />
              </div>

              <div className={styles.item} onClick={handleThemeClick}>
                {currentThemeIcon}
                <Typography component='p' variant='body2'>
                  {t('providerRoom:theme')}
                </Typography>
                <div className={styles.itemRight}>
                  <span className={styles.itemValue}>
                    {t(`providerRoom:${themePreferenceForUi}`, { defaultValue: themePreferenceForUi })}
                  </span>
                  <ChevronRight className={classnames(styles.right, { [styles.expanded]: isThemeOpen })} />
                </div>
              </div>

              {isThemeOpen && (
                <div className={styles.subItems}>
                  {themeOptions.map((option) => (
                    <div
                      key={option.mode}
                      className={classnames(styles.subItem, {
                        [styles.selected]: option.mode === themePreferenceForUi,
                      })}
                      onClick={() => handleThemeSelect(option.mode)}
                    >
                      {renderThemeIcon(option.mode)}
                      <Typography component='p' variant='body2'>
                        {option.label}
                      </Typography>
                    </div>
                  ))}
                </div>
              )}

              <div className={styles.item} onClick={handleLanguageClick}>
                <img src={selectedLanguageOption.flag} alt='' className={styles.flagIcon} />
                <Typography component='p' variant='body2'>
                  {t('providerRoom:languageLabel')}
                </Typography>
                <div className={styles.itemRight}>
                  <span className={styles.itemValue}>{selectedLanguageOption.label}</span>
                  <ChevronRight className={classnames(styles.right, { [styles.expanded]: isLanguageOpen })} />
                </div>
              </div>

              {isLanguageOpen && (
                <div className={styles.subItems}>
                  {languageOptions.map((option) => (
                    <div
                      key={option.code}
                      className={classnames(styles.subItem, {
                        [styles.selected]: option.code === selectedLanguageOption.code,
                      })}
                      onClick={() => handleLanguageSelect(option.code)}
                    >
                      <img src={option.flag} alt='' className={styles.flagIcon} />
                      <Typography component='p' variant='body2'>
                        {option.label}
                      </Typography>
                    </div>
                  ))}
                </div>
              )}

              <div className={styles.devider}></div>

              <div className={styles.item} onClick={handleHelpClick}>
                <HelpIcon />
                <Typography component='p' variant='body2'>
                  {t('auth:profileSectionMenu.help')}
                </Typography>
              </div>

              <div
                className={styles.item}
                onClick={() => {
                  setConnectionTestOpen(true);
                  hideMenu();
                }}
              >
                <NetworkCheckIcon />
                <Typography component='p' variant='body2'>
                  {t('providerRoom:connectionTest.menuLabel')}
                </Typography>
              </div>

              <div className={styles.devider}></div>

              <div className={styles.item} onClick={handleLogoutClick}>
                <LogOut2Icon />
                <Typography component='p' variant='body2'>
                  {t('auth:profileSectionMenu.logOut')}
                </Typography>
                <ChevronRight className={styles.right} />
              </div>
            </Stack>
          </Box>
        </Box>
      )}
      <ConnectionTestDialog open={connectionTestOpen} onClose={() => setConnectionTestOpen(false)} />
    </>
  );
}
